import {
    Add,
    AlertModal,
    Box,
    Button,
    Copy,
    Delete,
    Loader,
    Modal,
    Pagination,
    SearchBar,
    showToast,
    SubPageLayout,
    TextInput,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import ApplicationLayout from "components/layout/ApplicationLayout";
import { RULES_ROUTE } from "components/router/Routes";
import { EditableRulesTable } from "components/rules/EditableRulesTable";
import style from "components/rules/upliftValue.scss";
import {
    DESCRIPTION_MAX_LENGTH,
    HEADLINE_MAX_LENGTH,
    isUpliftValueValid,
    MAX_VALUE,
    validateUpliftValue,
} from "components/rules/upliftValueValidation";
import { EditableRow, useEditableRows } from "components/rules/useEditableRows";
import { TableFooter } from "components/table/TableFooter";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { UpliftRulesPage } from "domain/upliftRules";
import { UpliftRuleCreation, upliftRulesService } from "services/asset-management/uplift-rules/UpliftRulesService";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface UpliftValueRow extends EditableRow {
    // Missing on rows added in this view, which aren't saved yet.
    uuid?: string;
    headline: string;
    description: string;
    value: string;
    validated: boolean;
}

type EditableField = "headline" | "description" | "value";

const MAX_LENGTHS: Record<Exclude<EditableField, "value">, number> = {
    headline: HEADLINE_MAX_LENGTH,
    description: DESCRIPTION_MAX_LENGTH,
};

const rowIds = testIds.workArea.upliftValue.table.ruleRow;

const PAGE_SIZE = 100;
const MAX_PAGE_NUMBERS_TO_SHOW = 5;

const DEFAULT_CURRENCY = "USD";
const CURRENCY_SYMBOL = "$";

const formatValue = (value: string) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return value;
    }
    return new Intl.NumberFormat("en-US", { style: "currency", currency: DEFAULT_CURRENCY }).format(amount);
};

const isRowComplete = (row: UpliftValueRow) => isUpliftValueValid(row);

const trimRow = (row: UpliftValueRow) => ({
    ...row,
    headline: row.headline.trim(),
    description: row.description.trim(),
    value: row.value.trim(),
});

interface RuleChangeListProps {
    title: string;
    rules: UpliftValueRow[];
    hasDescriptionChanged?: (rule: UpliftValueRow) => boolean;
}

// Hide the section when nothing changed in it, so that the dialog lists only the actual changes.
const RuleChangeList = (props: RuleChangeListProps) => {
    const { t } = useTranslation();
    if (props.rules.length === 0) {
        return null;
    }
    const describeRule = (rule: UpliftValueRow) => {
        if (!props.hasDescriptionChanged?.(rule)) {
            return `${rule.headline} - ${formatValue(rule.value)}`;
        }
        const description = rule.description || t("UpliftValue.create.confirmationDialogForSaving.noDescription");
        return `${rule.headline} - ${description} - ${formatValue(rule.value)}`;
    };
    return (
        <>
            <TextBlock disableBottomSpacing>{props.title}</TextBlock>
            <ul>
                {props.rules.map((each) => (
                    <li key={each.id}>{describeRule(each)}</li>
                ))}
            </ul>
        </>
    );
};

function UpliftValueContent() {
    const { t } = useTranslation();
    const theme = useTheme();
    const editableRows = useEditableRows<UpliftValueRow>({
        createEmptyRow: (id) => ({
            id,
            headline: "",
            description: "",
            value: "",
            isEditing: true,
            isCreated: false,
            validated: false,
        }),
        isRowComplete,
        trimRow,
    });
    const rows = editableRows.rows;
    const [searchQuery, setSearchQuery] = React.useState<string>();
    const [currentPage, setCurrentPage] = React.useState(1);
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasFetchFailed, setHasFetchFailed] = React.useState(false);
    const [toShowAlertDialog, setToShowAlertDialog] = React.useState(false);
    const [touchedFields, setTouchedFields] = React.useState<Record<number, EditableField[]>>({});
    const [currentPageResult, setCurrentPageResult] = React.useState<UpliftRulesPage>();
    const [initialRows, setInitialRows] = React.useState<UpliftValueRow[]>([]);
    const [toCancelOperation, setToCancelOperation] = React.useState<boolean>(false);
    const [toConfirmSave, setToConfirmSave] = React.useState<boolean>(false);
    // Saved rules are fetched again so that new rules get their uuids and can be edited right away.
    const [reloadCount, setReloadCount] = React.useState(0);
    React.useEffect(() => {
        const abortController = new AbortController();
        setIsLoading(true);
        setHasFetchFailed(false);
        upliftRulesService
            .fetchUpliftRules(currentPage, PAGE_SIZE, searchQuery, abortController)
            .then((rules) => {
                const fetchedRules = rules.rules.map((rule) => ({
                    id: editableRows.createRowId(),
                    uuid: rule.uuid,
                    headline: rule.headline,
                    description: rule.description ?? "",
                    value: String(rule.value),
                    isEditing: false,
                    isCreated: false,
                    validated: true,
                }));
                editableRows.replaceRows(fetchedRules);
                setInitialRows(fetchedRules);
                setCurrentPageResult(rules);
                setCurrentPage(rules.currentPage);
            })
            .catch((error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                logger.error(`Failed to fetch uplift rules: ${error}`);
                editableRows.replaceRows([]);
                setHasFetchFailed(true);
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsLoading(false);
                }
            });
        return () => abortController.abort();
    }, [reloadCount, currentPage, searchQuery]);

    const markTouched = (rowId: number, field: EditableField) => {
        setTouchedFields((previous) => {
            const fields = previous[rowId] ?? [];
            return fields.includes(field) ? previous : { ...previous, [rowId]: [...fields, field] };
        });
    };

    const renderCells = (row: UpliftValueRow) => {
        if (!row.isEditing) {
            return [
                <span key="headline" className={style.truncatedItem}>
                    {row.headline}
                </span>,
                <span key="description" className={style.truncatedItem}>
                    {row.description}
                </span>,
                <span key="value">{formatValue(row.value)}</span>,
            ];
        }

        const errors = validateUpliftValue(row);
        const errorMessage = (field: EditableField) => {
            const error = errors[field];
            if (error === undefined) {
                return undefined;
            }
            if (error === "required" && !touchedFields[row.id]?.includes(field) && !row.validated) {
                return undefined;
            }
            return t(`UpliftValue.validation.${error}`, {
                field: t(`UpliftValue.${field}`),
                max: field === "value" ? formatValue(String(MAX_VALUE)) : MAX_LENGTHS[field],
            });
        };

        const textInput = (field: EditableField, testId: string, width?: number, leftSection?: React.ReactNode) => (
            <TextInput
                key={field}
                name={`uplift-value-${field}-${row.id}`}
                placeholder={t(`UpliftValue.${field}`)}
                value={row[field]}
                onChange={(event) => editableRows.updateRow(row.id, field, event.target.value)}
                onBlur={() => markTouched(row.id, field)}
                error={errorMessage(field)}
                hideLabelSection={true}
                testIds={{ input: testId }}
                width={width}
                leftSection={leftSection}
            />
        );

        return [
            textInput("headline", rowIds.headlineInput),
            textInput("description", rowIds.descriptionInput),
            textInput("value", rowIds.valueInput, undefined, CURRENCY_SYMBOL),
        ];
    };

    const deduceCreatedRows = () => rows.filter((each) => !each.isEditing && each.isCreated);

    const deduceEditedRows = () =>
        rows.filter((each) => {
            if (each.isEditing || each.isCreated || !each.uuid) {
                return false;
            }
            const saved = initialRows.find((initial) => initial.uuid === each.uuid);
            return (
                saved !== undefined &&
                (saved.headline !== each.headline ||
                    saved.description !== each.description ||
                    Number(saved.value) !== Number(each.value))
            );
        });

    const deduceRowsToReopen = () => {
        return [...deduceCreatedRows(), ...deduceEditedRows()].map((each) => each.id);
    };

    const hasDescriptionChanged = (row: UpliftValueRow) =>
        initialRows.find((initial) => initial.uuid === row.uuid)?.description !== row.description;

    const deduceDeletedRows = () => initialRows.filter((initial) => !rows.some((each) => each.uuid === initial.uuid));

    const hasRowInEditMode = () => rows.some((each) => each.isEditing);

    const disableSaveAction = () => {
        return (
            hasRowInEditMode() ||
            (deduceCreatedRows().length === 0 && deduceEditedRows().length === 0 && deduceDeletedRows().length === 0)
        );
    };

    const saveRules = () => {
        const toRule = (each: UpliftValueRow, action: UpliftRuleCreation["action"]): UpliftRuleCreation => ({
            uuid: each.uuid,
            headline: each.headline,
            description: each.description,
            value: Number(each.value),
            action,
        });
        const rulesToApply = [
            ...deduceCreatedRows().map((each) => toRule(each, "CREATE")),
            ...deduceEditedRows().map((each) => toRule(each, "EDIT")),
            ...deduceDeletedRows().map((each) => toRule(each, "DELETE")),
        ];
        if (rulesToApply.length > 0) {
            upliftRulesService
                .applyUpliftRules({ rules: rulesToApply }, new AbortController())
                .then(() => {
                    showToast(t("UpliftValue.create.success"), "success");
                    setReloadCount((previous) => previous + 1);
                })
                .catch(() => {
                    logger.error("Failed to apply the modified uplift rules.");
                    setToShowAlertDialog(true);
                    editableRows.reopenRows(deduceRowsToReopen());
                    editableRows.restoreRows(deduceDeletedRows());
                });
        }
    };

    const addRow = () => {
        editableRows.addRow();
    };

    const duplicateSelectedRows = () => {
        editableRows.duplicateRows(editableRows.selectedIds, (row) => ({ ...row, uuid: undefined, isCreated: true }));
    };

    const hasPendingChanges = () =>
        !disableSaveAction() ||
        rows.some((each) => each.isEditing || each.isCreated) ||
        rows.length !== initialRows.length;

    return (
        <SubPageLayout
            className={style.rulesSubPageLayout}
            title={""}
            loading={isLoading && currentPageResult === undefined}
            cancelButtonLabel={t("Common.cancel")}
            buttons={
                <Box justifyContent={"SpaceBetween"} gap="Sm">
                    <Button
                        variant="Primary"
                        onClick={() => {
                            setToConfirmSave(true);
                        }}
                        disabled={disableSaveAction()}
                        testId={testIds.workArea.upliftValue.saveRulesButton}
                    >
                        {t("Common.save")}
                    </Button>
                    <Button
                        variant="Secondary"
                        disabled={!hasPendingChanges()}
                        onClick={() => {
                            setToCancelOperation(true);
                        }}
                        testId={testIds.common.subpageView.cancelButton}
                    >
                        {t("Common.cancel")}
                    </Button>
                </Box>
            }
        >
            <Box columnLayout gap="Sm" className={style.content}>
                <TextBlock disableBottomSpacing>{t("UpliftValue.viewDescription")}</TextBlock>
                {hasFetchFailed && <TextBlock alignCenter>{t("UpliftValue.fetchError")}</TextBlock>}
                <Box verticalAlignment="Center" gap="Sm">
                    <SearchBar
                        name="uplift-value-search"
                        placeholder={t("Common.search")}
                        onSearch={(query: string) => {
                            if (hasPendingChanges()) {
                                setToCancelOperation(true);
                            }
                            if (!toCancelOperation && !hasPendingChanges()) {
                                setSearchQuery(query.trim() || undefined);
                                setCurrentPage(1);
                            }
                        }}
                        onClear={() => {
                            setSearchQuery(undefined);
                            setCurrentPage(1);
                        }}
                        searchOnChange={false}
                    />
                    <Button
                        variant="TertiaryDelete"
                        icon={<Delete />}
                        disabled={editableRows.selectedIds.length === 0}
                        onClick={() => editableRows.deleteRows(editableRows.selectedIds)}
                        testId={testIds.workArea.upliftValue.deleteButton}
                    >
                        {t("Common.delete")}
                    </Button>
                    <Button
                        variant="Tertiary"
                        icon={<Copy color={theme.iconFillColor} />}
                        disabled={editableRows.selectedIds.length === 0}
                        onClick={duplicateSelectedRows}
                        testId={testIds.workArea.upliftValue.duplicateButton}
                    >
                        {t("Common.duplicate")}
                    </Button>
                </Box>
                {isLoading && currentPageResult !== undefined && (
                    <Box horizontalAlignment="Center">
                        <Loader />
                    </Box>
                )}
                {!isLoading && (rows.length > 0 || searchQuery !== undefined) && (
                    <EditableRulesTable
                        name="uplift-value"
                        className={style.tableContainer}
                        testId={testIds.workArea.upliftValue.table.itself}
                        rowTestIds={rowIds}
                        emptyStateMessage={t("UpliftValue.emptySearchResult")}
                        selectAllLabel={t("UpliftValue.selectAll")}
                        headers={[t("UpliftValue.headline"), t("UpliftValue.description"), t("UpliftValue.value")]}
                        rows={rows}
                        editableRows={editableRows}
                        isRowComplete={isRowComplete}
                        renderCells={renderCells}
                    />
                )}
                <Box marginTop="Sm" marginBottom="Sm">
                    <Button
                        variant="Tertiary"
                        icon={<Add color={theme.iconFillColor} />}
                        onClick={addRow}
                        testId={testIds.workArea.upliftValue.addRuleButton}
                    >
                        {t("UpliftValue.addRule")}
                    </Button>
                </Box>
            </Box>
            {currentPageResult && (
                <TableFooter
                    className={style.footer}
                    rightColumn={
                        <Pagination
                            maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                            totalPages={currentPageResult.totalPages}
                            currentPage={currentPageResult.currentPage}
                            handlePageChange={setCurrentPage}
                            paginationText={t("UpliftValue.showingRules", {
                                start: initialRows.length > 0 ? (currentPageResult.currentPage - 1) * PAGE_SIZE + 1 : 0,
                                end: (currentPageResult.currentPage - 1) * PAGE_SIZE + initialRows.length,
                                total: currentPageResult.totalRules,
                            })}
                        />
                    }
                />
            )}

            <AlertModal
                title={t("UpliftValue.create.failureAlert.title")}
                variant={"Error"}
                isOpen={toShowAlertDialog}
                hideModal={() => setToShowAlertDialog(false)}
                buttons={
                    <Box gap={"Xs"}>
                        <Button
                            variant={"Primary"}
                            testId={testIds.common.dialog.closeButton}
                            onClick={() => setToShowAlertDialog(false)}
                        >
                            {t("Common.ok")}
                        </Button>
                    </Box>
                }
            >
                <TextBlock>{t("UpliftValue.create.failureAlert.message")}</TextBlock>
            </AlertModal>
            <Modal
                isOpen={toCancelOperation}
                hideModal={() => setToCancelOperation(false)}
                title={t("DeviceStatus.cancel.title")}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setToCancelOperation(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("DeviceStatus.cancel.keepEditing")}
                        </Button>
                        <Button
                            variant="Secondary"
                            onClick={() => {
                                editableRows.replaceRows(initialRows);
                                setToCancelOperation(false);
                            }}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("DeviceStatus.cancel.dontSave")}
                        </Button>
                    </>
                }
            >
                <TextBlock>{t("DeviceStatus.cancel.warning")}</TextBlock>
            </Modal>
            <Modal
                isOpen={toConfirmSave}
                hideModal={() => setToConfirmSave(false)}
                title={t("UpliftValue.create.confirmationDialogForSaving.title")}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => {
                                saveRules();
                                setToConfirmSave(false);
                            }}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("Common.save")}
                        </Button>
                        <Button
                            variant="Secondary"
                            onClick={() => {
                                setToConfirmSave(false);
                            }}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.cancel")}
                        </Button>
                    </>
                }
            >
                <TextBlock disableBottomSpacing>
                    {t("UpliftValue.create.confirmationDialogForSaving.message")}
                </TextBlock>
                <RuleChangeList
                    title={t("UpliftValue.create.confirmationDialogForSaving.newlyCreated")}
                    rules={deduceCreatedRows()}
                />
                <RuleChangeList
                    title={t("UpliftValue.create.confirmationDialogForSaving.editedRules")}
                    rules={deduceEditedRows()}
                    hasDescriptionChanged={hasDescriptionChanged}
                />
                <RuleChangeList
                    title={t("UpliftValue.create.confirmationDialogForSaving.deletedRules")}
                    rules={deduceDeletedRows()}
                />
            </Modal>
        </SubPageLayout>
    );
}

export default function UpliftValueView() {
    const { t } = useTranslation();
    return (
        <ApplicationLayout
            mainRoute={RULES_ROUTE}
            viewTitle={t("UpliftValue.viewTitle")}
            compactViewTitle
            view={<UpliftValueContent />}
        />
    );
}
